import get from 'lodash.get'
import React, { useEffect, useState } from 'react'
import './NonsenseWordRepetition.scss';
import { COPY_STRINGS } from "Constants/translatedCopy";
import { selectReaderManager } from "Components/Reader/ReaderManagerEvents/ReaderManagerSlice";
import { useDispatch, useSelector } from "react-redux";
import usePauseControl from "../../hooks/usePauseControl";
import useDidAttempt from '../../hooks/useDidAttempt';
import usePhraseTypeIndexes from '../../hooks/usePhraseTypeIndexes';
import { phraseTypes } from '../../../../../constants/constants';
import useEndingSpeech from '../../hooks/useEndingSpeech'
import { selectReaderVideo, replaceVideo, playVideo, enableReplay, disableReplay, resetEverything } from '../../../Video/readerVideoSlice';
import { getNextNonsenseWordRepetitionState } from './nonsenseWordRepetitionRunner';
import { nonsenseWordRepetitionStates } from './nonsenseWordRepetitionStates';
import { getUserData } from '../../../../../services/API';
import { parseSkipTranslationTags } from '../../../../../services/util';
import { getCopyByGradeband } from '../../../../../services/ThemingService';
import { logSessionInfo } from '../../../../../services/logger';

export default function NonsenseWordRepetition(props) {
  const { speech, currentPageIndex, nonsenseWord, phraseMetadata, isPlayingVerbalInstructions, startPhrase } = props
  const { paused, setWillHandlePause, doneHandlingPause, pausePhrase, resumePhrase, transcript } = props

  const STATES = { ...nonsenseWordRepetitionStates }
  const [currentState, setState] = useState(STATES.LOADING)

  const dispatch = useDispatch()
  const { donePlaying, replaying } = useSelector(selectReaderVideo)
  const { addToEndingSequence, hasPendingEndingSequence } = useEndingSpeech()
  const { didAttempt } = useDidAttempt({ transcript })
  const readerManagerStates = useSelector(selectReaderManager)
  const { isCurrentStoryStarted } = readerManagerStates;

  const { shouldHandlePause, finishHandlePause, passPauseControlBackToReaderManager } = usePauseControl({
    paused,
    setWillHandlePause,
    pausePhrase,
    resumePhrase,
    doneHandlingPause
  })

  const {
    phraseItems,
    phraseItemsTypes,
    isExamplePhraseOfThisType,
    isLastPhraseOfThisType,
    isLastPageOfStory,
  } = usePhraseTypeIndexes(currentPageIndex, phraseTypes.NONSENSE_WORD_REPETITION)

  const modelName = get(phraseMetadata, 'instructions.model') || 'my friend' // mapping model as the video model's name

  const goToNextState = () => {
    // Use functional setState to avoid stale closure issues
    // This ensures we always use the current state value, not a captured stale value
    setState(prevState => {
      const nextState = getNextNonsenseWordRepetitionState({
        currentState: prevState,
        isExamplePhraseOfThisType,
        didAttempt,
      });
      logSessionInfo('nwr_state_transition', { 
        prevState, 
        nextState, 
        isExamplePhraseOfThisType,
        nonsenseWord 
      });
      return nextState;
    })
  }

  useEffect(() => {
    return () => {
      dispatch(resetEverything())
    }
  }, [])

  useEffect(() => {
    if (currentState === STATES.LOADING) {
    } else if (currentState === STATES.PLAY_EXAMPLE_INTRO) {
      playExampleSpeech()
    } else if (currentState === STATES.PLAY_VIDEO) {
      playNonsenseVideo()
    } else if (currentState === STATES.PLAY_EXAMPLE_PROMPT) {
      playExamplePrompt()
    } else if (currentState === STATES.WAITING_FOR_USER_INPUT_AFTER_PROMPT) {
      waitingForUserInputAfterPrompt()
    } else if (currentState === STATES.PLAY_EXAMPLE_REPROMPT) {
      playExampleReprompt()
    } else if (currentState === STATES.PLAY_REPROMPT) {
      playReprompt()
    } else if (currentState === STATES.WAITING_FOR_USER_INPUT_AFTER_REPROMPT) {
      waitingForUserInputAfterReprompt()
    } else if (currentState === STATES.REPLAY_VIDEO) {
      replayNonsenseVideo()
    } else if (currentState === STATES.WAITING_FOR_USER_INPUT_AFTER_REPLAY) {
      waitingForUserInputAfterReplay()
    } else if (currentState === STATES.REGISTER_EXAMPLE_ENDING_SPEECH) {
      registerEndingSpeech()
    }

  }, [currentState])

  useEffect(() => {
    if (currentState === STATES.LOADING &&
      isCurrentStoryStarted &&
      !hasPendingEndingSequence &&
      !isPlayingVerbalInstructions) {

      goToNextState()
    }

  }, [currentState, hasPendingEndingSequence, isCurrentStoryStarted, isPlayingVerbalInstructions])

  useEffect(() => {
    if (didAttempt) {
      passPauseControlBackToReaderManager()
      dispatch(disableReplay())
      goToNextState()
    }
  }, [didAttempt])

  useEffect(() => {
    if (paused && shouldHandlePause) {
      goToNextState()
    }
  }, [paused, shouldHandlePause])

  useEffect(() => {
    // Only transition when video finishes AND we're in a video-playing state
    // This prevents stale closure issues and incorrect transitions from other states
    logSessionInfo('nwr_donePlaying_effect', { 
      donePlaying, 
      currentState, 
      willTransition: donePlaying && (currentState === STATES.PLAY_VIDEO || currentState === STATES.REPLAY_VIDEO),
      nonsenseWord
    });
    if (donePlaying && (currentState === STATES.PLAY_VIDEO || currentState === STATES.REPLAY_VIDEO)) {
      goToNextState()
    }
  }, [donePlaying, currentState])

  useEffect(() => {
    if (replaying) {
      setState(STATES.REPLAY_VIDEO)
    }
  }, [replaying])

  const playExampleSpeech = () => {
		const nonsense_word_repetition_copy = getCopyByGradeband(COPY_STRINGS, 'nonsense_word_repetition');

    const skipTranslation = getUserData().bilingualMode;
    const inText = nonsense_word_repetition_copy.intro({ modelName, nonsenseWord, skipTranslation });

    const onEnd = () => {
      goToNextState()
    };

    let parsedInstructions = parseSkipTranslationTags(inText);
    if (parsedInstructions.length > 1) {
      // We have skipTranslation tags
      let bilingualSequenceToSpeak = {
        textToSpeak: parsedInstructions,
        passThroughSpeechTags: true,
        onEnd,
      }
      speech.speakSequence(bilingualSequenceToSpeak);
    } else {
      speech.speak({
        textToSpeak: inText,
        onEnd
      }, false)
    }
  }

  const playNonsenseVideo = () => {
    logSessionInfo('nwr_play_video', { 
      videoURL: phraseMetadata.videoURL, 
      nonsenseWord,
      isExamplePhraseOfThisType
    });
    speech.stopListening()
    dispatch(replaceVideo(phraseMetadata.videoURL))
    dispatch(playVideo())
    // do not go to next state here, this is handled by side effect donePlaying
  }

  const playExamplePrompt = () => {
    logSessionInfo('nwr_play_example_prompt', { nonsenseWord });
		const nonsense_word_repetition_copy = getCopyByGradeband(COPY_STRINGS, 'nonsense_word_repetition');
		
    const skipTranslation = getUserData().bilingualMode;
    const inText = nonsense_word_repetition_copy.intro_prompt({ modelName, nonsenseWord, skipTranslation });

    let parsedInstructions = parseSkipTranslationTags(inText);
    if (parsedInstructions.length > 1) {
      // We have skipTranslation tags
      let bilingualSequenceToSpeak = {
        textToSpeak: parsedInstructions,
        passThroughSpeechTags: true,
        onEnd: () => {
          logSessionInfo('nwr_example_prompt_speech_ended', { nonsenseWord });
          goToNextState()
        },
      }
      speech.speakSequence(bilingualSequenceToSpeak);
    } else {
      speech.speak({
        textToSpeak: inText,
        onEnd: () => {
          logSessionInfo('nwr_example_prompt_speech_ended', { nonsenseWord });
          goToNextState()
        }
      }, true)
    }
  }

  const waitingForUserInputAfterPrompt = () => {
    logSessionInfo('nwr_waiting_for_input', { 
      action: 'enableReplay_and_startPhrase',
      nonsenseWord,
      isExamplePhraseOfThisType
    });
    dispatch(enableReplay())
    startPhrase()
    // do not go to next state here, this is handled by side effect didAttempt
  }

  const playExampleReprompt = () => {
    rePrompt(true)
    // go to next state is handled by rePrompt function after speech is done
  }

  const playReprompt = () => {
    rePrompt(false)
    // go to next state is handled by rePrompt function after speech is done
  }

  const waitingForUserInputAfterReprompt = () => {
    speech.startListening()
    // do not go to next state here, this is handled by side effect didAttempt
  }

  const replayNonsenseVideo = () => {
    speech.stopListening()
    pausePhrase()
    dispatch(disableReplay())
    // do not go to next state here, this is handled by side effect donePlaying
  }

  const waitingForUserInputAfterReplay = () => {
    passPauseControlBackToReaderManager()
    finishHandlePause()
    speech.startListening()
    // do not go to next state here, this is handled by side effect didAttempt
  }

  const rePrompt = (isExample) => {
		const nonsense_word_repetition_copy = getCopyByGradeband(COPY_STRINGS, 'nonsense_word_repetition');

    dispatch(disableReplay())

    const prompt = isExample ?
      nonsense_word_repetition_copy.prompt_to_try({ modelName }) : 
			nonsense_word_repetition_copy.subsequent_prompt_to_try

    speech.speak({
      textToSpeak: prompt,
      onEnd: () => {
        dispatch(enableReplay())
        passPauseControlBackToReaderManager()
        finishHandlePause()
        goToNextState()
      }
    }, true);
  }

  /**
   * register an ending speech for reader manager to play on end of phrase
   */
  const registerEndingSpeech = () => {
		const nonsense_word_repetition_copy = getCopyByGradeband(COPY_STRINGS, 'nonsense_word_repetition');
    if (isExamplePhraseOfThisType) {
      const skipTranslation = getUserData().bilingualMode;
      addToEndingSequence({ textToSpeak: nonsense_word_repetition_copy.intro_end({ modelName, nonsenseWord, skipTranslation }) })
    } else if (isLastPhraseOfThisType) {
      if (isLastPageOfStory) {
        addToEndingSequence({ textToSpeak: nonsense_word_repetition_copy.end })
      } else {
        addToEndingSequence({ textToSpeak: nonsense_word_repetition_copy.end })
      }
    } else { // the middle phrases
      addToEndingSequence({ textToSpeak: nonsense_word_repetition_copy.subsequent_end })
    }
  }

  return <div className='nonsense-word-repetition-item' />
}
